import { faCircle, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FC, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import styled from 'styled-components';
import { MetaLabels } from 'src/components/data-display/meta-label';
import { Stack } from 'src/components/layout/stack';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Hint } from 'src/components/text/hint';

export type StatusProps = {
  iconColor: string;
  status: ReactNode;
  info?: string;
  appendix?: ReactNode;
  /** Array of <a href="./?path=/docs/components-data-display-meta-label--docs">`MetaLabel`</a> items or a `ReactNode`. No need to wrap them using `MetaLabels`. */
  metaLabels?: ReactNode;
  inline?: boolean;
  'data-testid'?: string;
};

// Styles
const Icon = styled.span`
  display: inline;
`;

const Status = styled.span``;

const Info = styled.div<{ mode: StatusDisplayMode }>`
  display: flex;
  flex-direction: column;

  ${Status} {
    ${(props) =>
      props.mode === 'page' &&
      `
        font-weight: bold;
      `}
  }
`;

const InfoIcon: FC<{ status: ReactNode; info: string }> = ({
  status,
  info,
}) => {
  return (
    <>
      {status}{' '}
      <Tooltip content={info}>
        {(targetProps) => (
          <FontAwesomeIcon icon={faInfoCircle} {...targetProps} />
        )}
      </Tooltip>
    </>
  );
};

// Component
export const StatusDisplay: FC<StatusProps> = (props) => {
  const {
    iconColor,
    status,
    appendix,
    inline = false,
    metaLabels,
    info,
    ['data-testid']: testId = 'status-display',
  } = props;
  if (appendix && metaLabels) {
    throw new Error(
      'Expecting either appendix or metaLabels to be passed as prop, but can not include both together.'
    );
  }

  const contextMode = useStatusDisplayMode();

  const infoContent = (
    <>
      <Status data-testid={`${testId}-text`}>
        {info ? <InfoIcon status={status} info={info} /> : status}
      </Status>
      {appendix && (
        <Hint
          data-testid={`${testId}-appendix`}
          mode={contextMode === 'page' ? 'default' : 'small'}
        >
          {appendix}
        </Hint>
      )}
      {metaLabels && <MetaLabels>{metaLabels}</MetaLabels>}
    </>
  );

  return (
    <Stack
      data-testid={testId}
      gap={0.6}
      flow="column"
      alignItems="baseline"
      justifyContent="start"
    >
      <Icon>
        <FontAwesomeIcon
          icon={faCircle}
          size={contextMode === 'page' ? 'sm' : 'xs'}
          color={iconColor}
          data-testid={`${testId}-icon`}
          aria-hidden
        />
      </Icon>

      <Info mode={contextMode} data-testid={`${testId}-info`}>
        {inline ? (
          <Stack flow="column" gap={0.6} alignItems="baseline">
            <>{infoContent}</>
          </Stack>
        ) : (
          <>{infoContent}</>
        )}
      </Info>
    </Stack>
  );
};

// Provider
type StatusDisplayMode = 'page' | 'standalone';

const StatusDisplayContext = createContext<StatusDisplayMode>('standalone');

export function useStatusDisplayMode() {
  return useContext(StatusDisplayContext);
}

export const StatusDisplayProvider = StatusDisplayContext.Provider;
